import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, AlertTriangle, Bot, Loader2, Wand2, TrendingUp, Mic, Pencil, History, Download, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import BAIntroPanel from "./BAIntroPanel";
import RequirementToStoryWizard from "./RequirementToStoryWizard";
import ImpactAnalysisWizard from "./ImpactAnalysisWizard";
import MeetingIntelWizard from "./MeetingIntelWizard";
import { type UserStory, type AnalysisEntry } from "@/lib/projectStore";
import { useAuth } from "../context/AuthContext";
import CommentThread from "./CommentThread";

const BASE = process.env.REACT_APP_BACKEND_URL;

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  review: "bg-accent/15 text-accent",
  approved: "bg-primary/15 text-primary",
  rejected: "bg-destructive/15 text-destructive",
};

const typeLabels: Record<string, string> = {
  business: "Business",
  functional: "Functional",
  non_functional: "Non-Functional",
};

interface Props {
  mongoProjectId: string;
  projectName: string;
  team: { id: string; name: string }[];
  myRole?: "owner" | "editor" | "viewer";
  onStoriesGenerated: (stories: UserStory[]) => void;
  onAnalysisGenerated: (analysis: AnalysisEntry) => void;
  onBaProjectReady?: (baProjectId: string) => void;
}

export default function RequirementsPanel({ mongoProjectId, projectName, team, myRole, onStoriesGenerated, onAnalysisGenerated, onBaProjectReady }: Props) {
  const { token, user } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const [baProjectId, setBaProjectId] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiContextUsed, setAiContextUsed] = useState(false);
  const [contradictions, setContradictions] = useState<any[]>([]);
  const [wizardReq, setWizardReq] = useState<any | null>(null);
  const [impactWizardReq, setImpactWizardReq] = useState<any | null>(null);
  const [meetingWizardOpen, setMeetingWizardOpen] = useState(false);
  const [editingReq, setEditingReq] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [editSaving, setEditSaving] = useState(false);
  const [versionsReqId, setVersionsReqId] = useState<string | null>(null);
  const [versionsData, setVersionsData] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [filterDomain, setFilterDomain] = useState<string>("all");
  const [filterAssignee, setFilterAssignee] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const [form, setForm] = useState({
    title: "",
    raw_text: "",
    feature: "",
    channel: "",
    contains_phi: false,
    market: "",
    sensitivity_flags: "",
    req_type: "functional",
    assumptions: "",
    constraints_list: "",
    domain: "",
    priority: "should",
    risk_level: "",
    hazard: "",
  });

  useEffect(() => {
    const key = `ba-intro-seen-${mongoProjectId}`;
    if (!localStorage.getItem(key)) setShowIntro(true);
    initProject();
  }, [mongoProjectId]);

  const initProject = async () => {
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/projects/by-mongo/${mongoProjectId}?name=${encodeURIComponent(projectName)}`,
        { headers: authHeader }
      );
      setBaProjectId(data.id);
      setInitError(null);
      onBaProjectReady?.(data.id);
      loadRequirements(data.id);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Unknown error";
      setInitError(msg);
      setLoading(false);
    }
  };

  const loadRequirements = async (pid: string) => {
    try {
      const { data } = await axios.get(`${BASE}/api/tools/projects/${pid}`, { headers: authHeader });
      setRequirements(data.requirements || []);
    } catch (e) {
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissIntro = () => {
    localStorage.setItem(`ba-intro-seen-${mongoProjectId}`, "1");
    setShowIntro(false);
  };

  const handleAiStructure = async () => {
    if (!rawText.trim()) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/structure-requirement`,
        { raw_text: rawText, project_id: baProjectId },
        { headers: authHeader }
      );
      // Pre-fill the requirement form
      setForm({
        title: data.title || "",
        raw_text: rawText,
        feature: data.feature || "",
        channel: data.channel || "",
        contains_phi: data.contains_phi || false,
        market: data.market || "",
        sensitivity_flags: (data.sensitivity_flags || []).join(", "),
        req_type: data.req_type || "functional",
        assumptions: (data.assumptions || []).join("\n"),
        constraints_list: (data.constraints_list || []).join("\n"),
        domain: data.domain || "",
        priority: data.priority || "should",
        risk_level: "",
        hazard: "",
      });
      setAiContextUsed(data.rag_context_used || false);
      // Run contradiction check in parallel
      try {
        const { data: contData } = await axios.post(
          `${BASE}/api/tools/requirements/check-contradictions`,
          { raw_text: rawText, project_id: baProjectId },
          { headers: authHeader }
        );
        setContradictions(contData.contradictions || []);
      } catch { setContradictions([]); }
      setAiOpen(false);
      setRawText("");
      setOpen(true); // open the New Requirement dialog pre-filled
    } catch (e: any) {
      setAiError(e?.response?.data?.detail || e?.message || "AI structuring failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAssign = async (reqId: string, assignedTo: string | null) => {
    await axios.patch(
      `${BASE}/api/tools/requirements/${reqId}/assign`,
      { assigned_to: assignedTo || null },
      { headers: authHeader }
    );
    setRequirements((prev) =>
      prev.map((r) => r.id === reqId ? { ...r, assigned_to: assignedTo } : r)
    );
  };

  const openEdit = (req: any) => {
    setEditingReq(req);
    setEditForm({
      title: req.title || "",
      raw_text: req.raw_text || "",
      feature: req.feature || "",
      channel: req.channel || "",
      contains_phi: req.contains_phi || false,
      market: req.market || "",
      sensitivity_flags: (req.sensitivity_flags || []).join(", "),
      req_type: req.req_type || "functional",
      assumptions: (req.assumptions || []).join("\n"),
      constraints_list: (req.constraints_list || []).join("\n"),
      domain: req.domain || "",
      priority: req.priority || "should",
      status: req.status || "draft",
      risk_level: req.risk_level || "",
      hazard: req.hazard || "",
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${BASE}/api/tools/requirements/${id}`, { headers: authHeader });
      setRequirements(prev => prev.filter(r => r.id !== id));
    } catch (e) {
      console.error("Failed to delete requirement", e);
    }
  };

  const handleEditSave = async () => {
    if (!editingReq || !baProjectId) return;
    setEditSaving(true);
    try {
      await axios.patch(
        `${BASE}/api/tools/requirements/${editingReq.id}`,
        {
          title: editForm.title.trim() || undefined,
          raw_text: editForm.raw_text || undefined,
          feature: editForm.feature || undefined,
          channel: editForm.channel || undefined,
          contains_phi: editForm.contains_phi,
          market: editForm.market || undefined,
          sensitivity_flags: editForm.sensitivity_flags.split(",").map((s: string) => s.trim()).filter(Boolean),
          req_type: editForm.req_type,
          assumptions: editForm.assumptions.split("\n").map((s: string) => s.trim()).filter(Boolean),
          constraints_list: editForm.constraints_list.split("\n").map((s: string) => s.trim()).filter(Boolean),
          domain: editForm.domain || undefined,
          priority: editForm.priority || undefined,
          status: editForm.status,
          risk_level: editForm.risk_level || undefined,
          hazard: editForm.hazard || undefined,
        },
        { headers: authHeader }
      );
      setEditingReq(null);
      loadRequirements(baProjectId);
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setEditSaving(false);
    }
  };

  const toggleVersions = async (reqId: string) => {
    if (versionsReqId === reqId) {
      setVersionsReqId(null);
      setVersionsData([]);
      return;
    }
    setVersionsReqId(reqId);
    setVersionsLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE}/api/tools/requirements/${reqId}/versions`,
        { headers: authHeader }
      );
      setVersionsData(data);
    } catch {
      setVersionsData([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const exportCsv = (reqs: any[]) => {
    const cols = ["id", "title", "req_type", "status", "priority", "domain", "feature", "channel", "market", "contains_phi", "assigned_to", "raw_text", "assumptions", "constraints_list", "version", "created_at"];
    const escape = (v: any) => {
      const s = Array.isArray(v) ? v.join("; ") : String(v ?? "");
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = [cols.join(","), ...reqs.map(r => cols.map(c => escape(r[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `requirements-${baProjectId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !baProjectId) return;
    await axios.post(`${BASE}/api/tools/requirements`, {
      project_id: baProjectId,
      title: form.title.trim(),
      raw_text: form.raw_text,
      feature: form.feature,
      channel: form.channel || null,
      contains_phi: form.contains_phi,
      market: form.market || null,
      sensitivity_flags: form.sensitivity_flags.split(",").map(s => s.trim()).filter(Boolean),
      req_type: form.req_type,
      assumptions: form.assumptions.split("\n").map(s => s.trim()).filter(Boolean),
      constraints_list: form.constraints_list.split("\n").map(s => s.trim()).filter(Boolean),
      domain: form.domain || null,
      priority: form.priority || null,
      risk_level: form.risk_level || null,
      hazard: form.hazard || null,
    }, { headers: authHeader });
    setForm({ title: "", raw_text: "", feature: "", channel: "", contains_phi: false, market: "", sensitivity_flags: "", req_type: "functional", assumptions: "", constraints_list: "", domain: "", priority: "should", risk_level: "", hazard: "" });
    setContradictions([]);
    setOpen(false);
    loadRequirements(baProjectId);
  };

  const filteredRequirements = requirements.filter((req: any) => {
    if (filterDomain !== "all") {
      if (filterDomain === "__none" && req.domain) return false;
      if (filterDomain !== "__none" && req.domain !== filterDomain) return false;
    }
    if (filterStatus !== "all" && req.status !== filterStatus) return false;
    if (filterAssignee !== "all") {
      if (filterAssignee === "__unassigned" && req.assigned_to) return false;
      if (filterAssignee !== "__unassigned" && req.assigned_to !== filterAssignee) return false;
    }
    return true;
  });

  if (showIntro) {
    return <BAIntroPanel projectName={projectName} onDismiss={handleDismissIntro} />;
  }

  if (wizardReq) {
    return (
      <div className="h-full flex flex-col">
        <RequirementToStoryWizard
          requirement={wizardReq}
          onConfirm={(stories) => {
            setWizardReq(null);
            onStoriesGenerated(stories);
          }}
          onClose={() => setWizardReq(null)}
        />
      </div>
    );
  }

  if (impactWizardReq) {
    return (
      <div className="h-full flex flex-col">
        <ImpactAnalysisWizard
          requirement={impactWizardReq}
          onConfirm={(analysis) => {
            setImpactWizardReq(null);
            onAnalysisGenerated(analysis);
          }}
          onClose={() => setImpactWizardReq(null)}
        />
      </div>
    );
  }

  if (meetingWizardOpen && baProjectId) {
    return (
      <div className="h-full flex flex-col">
        <MeetingIntelWizard
          baProjectId={baProjectId}
          onConfirm={async (reqs) => {
            for (const req of reqs) {
              await axios.post(`${BASE}/api/tools/requirements`, {
                project_id: baProjectId,
                title: req.title.trim(),
                raw_text: req.raw_text,
                feature: req.feature,
                channel: req.channel || null,
                contains_phi: req.contains_phi,
                market: req.market || null,
                sensitivity_flags: req.sensitivity_flags.split(",").map((s: string) => s.trim()).filter(Boolean),
                req_type: req.req_type,
                assumptions: req.assumptions.split("\n").map((s: string) => s.trim()).filter(Boolean),
                constraints_list: req.constraints_list.split("\n").map((s: string) => s.trim()).filter(Boolean),
              }, { headers: authHeader });
            }
            setMeetingWizardOpen(false);
            loadRequirements(baProjectId);
          }}
          onClose={() => setMeetingWizardOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Requirements</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{requirements.length} total</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={!baProjectId}
            onClick={() => setMeetingWizardOpen(true)}
          >
            <Mic className="h-3.5 w-3.5" /> From Meeting Notes
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={filteredRequirements.length === 0}
            onClick={() => exportCsv(filteredRequirements)}
            title="Export filtered requirements to CSV"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
          <Dialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); setAiError(null); setRawText(""); }}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1.5" disabled={!baProjectId}>
                <Bot className="h-3.5 w-3.5" /> AI Structure
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">Structure with AI</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <p className="text-xs text-muted-foreground">
                  Paste raw notes, a stakeholder email, or meeting transcript. Llama 3.1 will extract structured requirement fields. Your knowledge base will be searched for relevant policy context.
                </p>
                <Textarea
                  placeholder="e.g. We need to notify Florida members when their claim is approved. The message should include the claim amount and go out by email. Legal flagged this as sensitive..."
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  rows={6}
                />
                <p className="text-xs text-muted-foreground text-right">{rawText.length} characters</p>
                {aiError && (
                  <p className="text-xs text-destructive">{aiError}</p>
                )}
                <Button onClick={handleAiStructure} disabled={!rawText.trim() || aiLoading} className="w-full gap-1.5">
                  {aiLoading ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Structuring with Llama 3.1...</>
                  ) : (
                    <><Bot className="h-3.5 w-3.5" /> Structure & Pre-fill Form</>
                  )}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Fields will be pre-filled for your review — nothing saves until you confirm
                </p>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setAiContextUsed(false); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5" disabled={!baProjectId} title={initError || undefined}>
                <Plus className="h-3.5 w-3.5" /> New Requirement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display">New Requirement</DialogTitle>
                {aiContextUsed && (
                  <p className="text-xs text-accent mt-1 flex items-center gap-1">
                    <Bot className="h-3 w-3" /> Pre-filled by Mixtral using your knowledge base
                  </p>
                )}
              </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Requirement title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              <Textarea placeholder="Raw text / notes / meeting excerpt" value={form.raw_text} onChange={e => setForm({ ...form, raw_text: e.target.value })} rows={3} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Feature (e.g. claim notification)" value={form.feature} onChange={e => setForm({ ...form, feature: e.target.value })} />
                <Input placeholder="Channel (email, sms, portal)" value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Market / State (e.g. Florida)" value={form.market} onChange={e => setForm({ ...form, market: e.target.value })} />
                <Select value={form.req_type} onValueChange={v => setForm({ ...form, req_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="functional">Functional</SelectItem>
                    <SelectItem value="non_functional">Non-Functional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="phi" checked={form.contains_phi} onChange={e => setForm({ ...form, contains_phi: e.target.checked })} className="h-4 w-4" />
                <label htmlFor="phi" className="text-sm text-foreground">Contains PHI</label>
              </div>
              <Input placeholder="Sensitivity flags (comma-separated: breach, payment_amount)" value={form.sensitivity_flags} onChange={e => setForm({ ...form, sensitivity_flags: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Domain</p>
                  <Input placeholder="e.g. billing, clinical, auth…" value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Priority</p>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="must">Must Have</SelectItem>
                      <SelectItem value="should">Should Have</SelectItem>
                      <SelectItem value="could">Could Have</SelectItem>
                      <SelectItem value="wont">Won't Have</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea placeholder="Assumptions (one per line)" value={form.assumptions} onChange={e => setForm({ ...form, assumptions: e.target.value })} rows={2} />
              <Textarea placeholder="Constraints (one per line)" value={form.constraints_list} onChange={e => setForm({ ...form, constraints_list: e.target.value })} rows={2} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Risk Level (ISO 14971)</p>
                  <Select value={form.risk_level} onValueChange={v => setForm({ ...form, risk_level: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Hazard</p>
                  <Input placeholder="e.g. PHI exposure, incorrect dose" value={form.hazard} onChange={e => setForm({ ...form, hazard: e.target.value })} />
                </div>
              </div>
              {contradictions.length > 0 && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-destructive">⚠ Policy Contradictions Detected</p>
                  {contradictions.map((c: any, i: number) => (
                    <div key={i} className="text-xs text-destructive/80 space-y-0.5">
                      <p className="font-medium">[{c.severity?.toUpperCase()}] {c.conflict_type?.replace(/_/g, " ")}</p>
                      <p className="text-muted-foreground">{c.kb_policy}</p>
                      <p className="font-medium">Fix: {c.recommendation}</p>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={handleCreate} className="w-full">Create Requirement</Button>
            </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {initError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive space-y-1">
          <p className="font-semibold">Analysis tools are temporarily unavailable</p>
          <p className="text-muted-foreground">This usually resolves itself within a minute or two — please try again shortly.</p>
          <button onClick={initProject} className="underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Filters */}
      {requirements.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            className="h-7 text-xs border border-input rounded-md px-2 bg-background"
          >
            <option value="all">All domains</option>
            {Array.from(new Set(requirements.map((r: any) => r.domain).filter(Boolean))).sort().map((d: any) => (
              <option key={d} value={d}>{d}</option>
            ))}
            <option value="__none">No domain</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-7 text-xs border border-input rounded-md px-2 bg-background"
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {team.length > 0 && (
            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="h-7 text-xs border border-input rounded-md px-2 bg-background"
            >
              <option value="all">All assignees</option>
              {team.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              <option value="__unassigned">Unassigned</option>
            </select>
          )}
        </div>
      )}

      {/* Edit Requirement Dialog */}
      <Dialog open={!!editingReq} onOpenChange={(v) => { if (!v) setEditingReq(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Requirement</DialogTitle>
            <p className="text-xs text-muted-foreground">Saving will snapshot the current version before overwriting.</p>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input placeholder="Requirement title *" value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} />
            <Textarea placeholder="Raw text / notes / meeting excerpt" value={editForm.raw_text || ""} onChange={e => setEditForm({ ...editForm, raw_text: e.target.value })} rows={3} />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Feature" value={editForm.feature || ""} onChange={e => setEditForm({ ...editForm, feature: e.target.value })} />
              <Input placeholder="Channel" value={editForm.channel || ""} onChange={e => setEditForm({ ...editForm, channel: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Market / State" value={editForm.market || ""} onChange={e => setEditForm({ ...editForm, market: e.target.value })} />
              <Select value={editForm.req_type || "functional"} onValueChange={v => setEditForm({ ...editForm, req_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="functional">Functional</SelectItem>
                  <SelectItem value="non_functional">Non-Functional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={editForm.status || "draft"} onValueChange={v => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={editForm.priority || "should"} onValueChange={v => setEditForm({ ...editForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="must">Must Have</SelectItem>
                  <SelectItem value="should">Should Have</SelectItem>
                  <SelectItem value="could">Could Have</SelectItem>
                  <SelectItem value="wont">Won't Have</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-phi" checked={editForm.contains_phi || false} onChange={e => setEditForm({ ...editForm, contains_phi: e.target.checked })} className="h-4 w-4" />
              <label htmlFor="edit-phi" className="text-sm text-foreground">Contains PHI</label>
            </div>
            <Input placeholder="Domain (e.g. billing, clinical)" value={editForm.domain || ""} onChange={e => setEditForm({ ...editForm, domain: e.target.value })} />
            <Input placeholder="Sensitivity flags (comma-separated)" value={editForm.sensitivity_flags || ""} onChange={e => setEditForm({ ...editForm, sensitivity_flags: e.target.value })} />
            <Textarea placeholder="Assumptions (one per line)" value={editForm.assumptions || ""} onChange={e => setEditForm({ ...editForm, assumptions: e.target.value })} rows={2} />
            <Textarea placeholder="Constraints (one per line)" value={editForm.constraints_list || ""} onChange={e => setEditForm({ ...editForm, constraints_list: e.target.value })} rows={2} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Risk Level (ISO 14971)</p>
                <Select value={editForm.risk_level || ""} onValueChange={v => setEditForm({ ...editForm, risk_level: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Hazard</p>
                <Input placeholder="e.g. PHI exposure" value={editForm.hazard || ""} onChange={e => setEditForm({ ...editForm, hazard: e.target.value })} />
              </div>
            </div>
            <Button onClick={handleEditSave} disabled={editSaving || !editForm.title?.trim()} className="w-full gap-1.5">
              {editSaving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {(() => {
        const filtered = filteredRequirements;

        const grouped = filtered.reduce((acc: Record<string, any[]>, req: any) => {
          const key = req.domain || "Ungrouped";
          if (!acc[key]) acc[key] = [];
          acc[key].push(req);
          return acc;
        }, {});

        const priorityColors: Record<string, string> = {
          must:   "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:text-red-400 dark:border-red-700/40",
          should: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-700/40",
          could:  "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-700/40",
          wont:   "bg-muted text-muted-foreground border-border",
        };

        return (
      <>
      {loading ? (
        <div className="text-center py-16 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm font-semibold text-foreground mb-1">{requirements.length === 0 ? "No requirements yet" : "No requirements match filters"}</p>
          <p className="text-xs text-muted-foreground">{requirements.length === 0 ? "Create your first structured requirement above." : "Try adjusting the filters."}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).sort(([a], [b]) => a === "Ungrouped" ? 1 : b === "Ungrouped" ? -1 : a.localeCompare(b)).map(([domain, reqs]) => (
            <div key={domain}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-mono font-semibold text-muted-foreground uppercase tracking-wider">{domain}</span>
                <span className="text-[10px] font-mono text-muted-foreground/60">{(reqs as any[]).length}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-2">
          {(reqs as any[]).map((req: any) => (
            <div key={req.id} className="border border-border rounded-md p-4 bg-card space-y-2">
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-semibold text-card-foreground font-body">{req.title}</span>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {req.priority && (
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${priorityColors[req.priority] || ""}`}>{req.priority}</Badge>
                  )}
                  <Badge variant="outline" className={`text-[10px] px-1.5 ${statusColors[req.status]}`}>{req.status}</Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5">{typeLabels[req.req_type] || req.req_type}</Badge>
                  {req.contains_phi && (
                    <Badge variant="outline" className="text-[10px] px-1.5 bg-destructive/10 text-destructive border-destructive/30 gap-0.5">
                      <AlertTriangle className="h-2.5 w-2.5" /> PHI
                    </Badge>
                  )}
                  {req.risk_level && (
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${
                      req.risk_level === "critical" ? "bg-destructive/15 text-destructive border-destructive/30" :
                      req.risk_level === "high" ? "bg-orange-100 text-orange-700 border-orange-300" :
                      req.risk_level === "medium" ? "bg-yellow-100 text-yellow-700 border-yellow-300" :
                      "bg-green-100 text-green-700 border-green-300"
                    }`}>⚠ {req.risk_level}</Badge>
                  )}
                  <button
                    onClick={() => { if (window.confirm("Delete this requirement?")) handleDelete(req.id); }}
                    title="Delete requirement"
                    className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-3 text-[11px] text-muted-foreground font-mono flex-wrap">
                  {req.feature && <span>feature: {req.feature}</span>}
                  {req.channel && <span>channel: {req.channel}</span>}
                  {req.market && <span>market: {req.market}</span>}
                  <span className="text-muted-foreground/50">v{req.version ?? 1}</span>
                </div>
                <div className="flex gap-1.5 flex-wrap justify-end">
                  {team.length > 0 && (
                    <select
                      value={req.assigned_to || ""}
                      onChange={(e) => handleAssign(req.id, e.target.value || null)}
                      className="h-7 text-[11px] border border-input rounded-md px-2 bg-background text-foreground shrink-0"
                      title="Assign to BA"
                    >
                      <option value="">Unassigned</option>
                      {team.map((m) => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 gap-1 text-[11px] shrink-0"
                    onClick={() => openEdit(req)}
                    title="Edit this requirement"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 gap-1 text-[11px] shrink-0"
                    onClick={() => toggleVersions(req.id)}
                    title="View version history"
                  >
                    <History className="h-3 w-3" />
                    {versionsReqId === req.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 gap-1 text-[11px] shrink-0"
                    onClick={() => setImpactWizardReq(req)}
                    title="Draft an impact analysis for this requirement"
                  >
                    <TrendingUp className="h-3 w-3" /> Impact
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 gap-1 text-[11px] shrink-0"
                    onClick={() => setWizardReq(req)}
                    title="Open the step-by-step story generation wizard for this requirement"
                  >
                    <Wand2 className="h-3 w-3" /> Stories
                  </Button>
                </div>
              </div>
              {/* Version history panel */}
              {versionsReqId === req.id && (
                <div className="mt-2 pt-2 border-t border-border space-y-2">
                  {versionsLoading ? (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading history…</p>
                  ) : versionsData.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">No prior versions — this is the original.</p>
                  ) : (
                    versionsData.map((v: any) => (
                      <div key={v.id} className="rounded border border-border bg-muted/30 p-2 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-mono font-semibold text-muted-foreground">v{v.version}</span>
                          <span className="text-[10px] text-muted-foreground/60">{new Date(v.changed_at).toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-foreground font-semibold">{v.snapshot?.title}</p>
                        <div className="flex gap-2 text-[10px] text-muted-foreground font-mono flex-wrap">
                          {v.snapshot?.status && <span>status: {v.snapshot.status}</span>}
                          {v.snapshot?.priority && <span>priority: {v.snapshot.priority}</span>}
                          {v.snapshot?.domain && <span>domain: {v.snapshot.domain}</span>}
                          {v.snapshot?.req_type && <span>type: {v.snapshot.req_type}</span>}
                        </div>
                        {v.snapshot?.raw_text && (
                          <p className="text-[10px] text-muted-foreground line-clamp-2">{v.snapshot.raw_text}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
              <CommentThread
                projectId={mongoProjectId}
                entityType="requirement"
                entityId={req.id}
                currentUserId={user?.id}
                myRole={myRole}
              />
            </div>
          ))}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
        );
      })()}
    </div>
  );
}
